import { memo, useEffect, useLayoutEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import {
  VIDEO_EDITOR_CLIP_COLORS,
  VIDEO_EDITOR_CLIP_VIDEO_BORDER,
  VIDEO_EDITOR_CLIP_VIDEO_HALO,
  VIDEO_EDITOR_TRACK_TYPE,
} from "./constants";
import { CLIP_INTERACTION_MODE } from "./timelineInteractionUtils";
import { drawFilmstripToCanvas } from "./videoEditorFilmstrip";
import { useTranslationApp } from "../../lib/i18next";
import {
  BUTTON_DELETE,
  MODAL_VIDEO_EDITOR_CLIP_ACTIONS,
  MODAL_VIDEO_EDITOR_SPLIT_CLIP,
} from "../../i18n/keysTranslations";

const AudioWaveformBars = () => (
  <div className="video-editor-clip-waveform" aria-hidden="true">
    {Array.from({ length: 28 }).map((_, index) => (
      <span
        key={index}
        style={{ height: `${28 + ((index * 13) % 62)}%` }}
      />
    ))}
  </div>
);

const VideoFilmstrip = ({ sourceUrl, inPoint, outPoint, frozen = false }) => {
  const canvasRef = useRef(null);
  const [hasFrames, setHasFrames] = useState(false);
  const roundedInPoint = Number(inPoint || 0).toFixed(2);
  const roundedOutPoint = Number(outPoint || 0).toFixed(2);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !sourceUrl || frozen) {
      return undefined;
    }

    const controller = new AbortController();

    const onDraw = async () => {
      try {
        const count = await drawFilmstripToCanvas({
          canvas,
          sourceUrl,
          inPoint: Number(roundedInPoint),
          outPoint: Number(roundedOutPoint),
          signal: controller.signal,
        });
        if (!controller.signal.aborted) {
          setHasFrames(count > 0);
        }
      } catch {
        if (!controller.signal.aborted) {
          setHasFrames(false);
        }
      }
    };

    onDraw();

    return () => {
      controller.abort();
    };
  }, [frozen, roundedInPoint, roundedOutPoint, sourceUrl]);

  if (!sourceUrl) {
    return <div className="video-editor-clip-filmstrip-fallback" />;
  }

  return (
    <div className="video-editor-clip-filmstrip">
      <canvas
        ref={canvasRef}
        style={{
          display: "block",
          height: "100%",
          width: "100%",
          opacity: hasFrames ? 1 : 0.4,
        }}
      />
    </div>
  );
};

const ImageClipPreview = ({ sourceUrl, label }) => (
  <div className="video-editor-clip-image">
    {sourceUrl ? (
      <img src={sourceUrl} alt={label} draggable={false} />
    ) : null}
  </div>
);

const ClipActionsChip = ({
  isClipHovered,
  canSplit,
  onSplitClip,
  onDeleteClip,
  isPurple = false,
}) => {
  const { t } = useTranslationApp();
  const [anchorEl, setAnchorEl] = useState(null);
  const isMenuOpen = Boolean(anchorEl);
  const [isChipHovered, setIsChipHovered] = useState(false);
  const isVisible = isPurple || isClipHovered || isChipHovered || isMenuOpen;

  const onCloseMenu = () => {
    setAnchorEl(null);
    setIsChipHovered(false);
  };

  return (
    <Box
      data-video-editor-clip-menu="true"
      onMouseEnter={() => setIsChipHovered(true)}
      onMouseLeave={() => {
        if (!isMenuOpen) {
          setIsChipHovered(false);
        }
      }}
      onPointerDown={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      className="video-editor-clip-menu"
      sx={{
        opacity: isVisible ? 1 : 0,
        pointerEvents: isVisible ? "auto" : "none",
      }}
    >
      <IconButton
        aria-label={t(MODAL_VIDEO_EDITOR_CLIP_ACTIONS)}
        onClick={(event) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
        }}
        sx={{
          width: 22,
          height: 22,
          p: 0,
          borderRadius: "6px",
          bgcolor: "#fff",
          color: "#111",
          boxShadow: "0 0 0 1px rgba(17, 24, 39, 0.12)",
          "&:hover": {
            bgcolor: "#f3f4f6",
          },
        }}
      >
        <MoreHorizIcon sx={{ fontSize: 16, color: "#111" }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={onCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{
          onClick: (event) => event.stopPropagation(),
          onMouseDown: (event) => event.stopPropagation(),
        }}
      >
        <MenuItem
          disabled={!canSplit}
          onClick={(event) => {
            event.stopPropagation();
            onSplitClip();
            onCloseMenu();
          }}
        >
          {t(MODAL_VIDEO_EDITOR_SPLIT_CLIP)}
        </MenuItem>
        <MenuItem
          onClick={(event) => {
            event.stopPropagation();
            onDeleteClip();
            onCloseMenu();
          }}
        >
          {t(BUTTON_DELETE)}
        </MenuItem>
      </Menu>
    </Box>
  );
};

const getClipChrome = ({ type, isSelected }) => {
  if (type === VIDEO_EDITOR_TRACK_TYPE.VIDEO) {
    return {
      borderColor: VIDEO_EDITOR_CLIP_VIDEO_BORDER,
      boxShadow: isSelected
        ? `0 0 0 2px ${VIDEO_EDITOR_CLIP_VIDEO_HALO}`
        : "0 1px 2px rgba(32, 37, 45, 0.18)",
    };
  }

  return {
    borderColor:
      VIDEO_EDITOR_CLIP_COLORS[type] ||
      VIDEO_EDITOR_CLIP_COLORS[VIDEO_EDITOR_TRACK_TYPE.VIDEO],
    boxShadow: isSelected
      ? `0 0 0 2px ${VIDEO_EDITOR_CLIP_COLORS[type]}55`
      : "0 1px 2px rgba(32, 37, 45, 0.12)",
  };
};

const VideoEditorClipAction = ({
  action,
  isSelected = false,
  isDragging = false,
  canSplit = false,
  frozenPreview = false,
  onSplitClip = () => {},
  onDeleteClip = () => {},
  onPointerDownClip = () => {},
  onSelectClip = () => {},
  onCommitClipText = () => {},
}) => {
  const type = action?.data?.type;
  const label = action?.data?.text || action?.data?.label || "";
  const sourceUrl = action?.data?.sourceUrl || "";
  const inPoint = action?.data?.inPoint || 0;
  const outPoint = action?.data?.outPoint || 0;
  const clipStart = action?.start || 0;
  const clipDuration = Math.max((action?.end || 0) - clipStart, 0);
  const durationLabel = `${Math.max(0, clipDuration).toFixed(1)}s`;
  const { borderColor, boxShadow } = getClipChrome({ type, isSelected });
  const [isClipHovered, setIsClipHovered] = useState(false);
  const [draftText, setDraftText] = useState(label);
  const inputRef = useRef(null);
  const ignoreBlurRef = useRef(false);
  const isSubtitle = type === VIDEO_EDITOR_TRACK_TYPE.SUBTITLE;
  const isEditingSubtitle = isSubtitle && isSelected;
  const isCompact = clipDuration < 1.4;

  useEffect(() => {
    setDraftText(label);
  }, [label]);

  useLayoutEffect(() => {
    if (!isEditingSubtitle) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      const node = inputRef.current;
      if (!node) {
        return;
      }
      node.focus();
    }, 50);

    return () => window.clearTimeout(timeoutId);
  }, [action?.id, isEditingSubtitle]);

  const ariaLabel = `${type}, ${label}, ${clipStart.toFixed(1)}s–${(
    clipStart + clipDuration
  ).toFixed(1)}s`;

  const onCommitText = () => {
    if (ignoreBlurRef.current) {
      ignoreBlurRef.current = false;
      setDraftText(label);
      return;
    }
    const nextText = String(draftText || "").trim();
    if (!nextText) {
      setDraftText(label);
      return;
    }
    if (nextText !== label) {
      onCommitClipText({ clipId: action?.id, text: nextText });
    }
  };

  return (
    <Box
      role="group"
      tabIndex={isEditingSubtitle ? -1 : 0}
      aria-label={ariaLabel}
      aria-selected={isSelected}
      data-video-editor-clip="true"
      data-clip-id={action?.id}
      title={`${label} · ${durationLabel}`}
      onMouseEnter={() => setIsClipHovered(true)}
      onMouseLeave={() => setIsClipHovered(false)}
      onPointerDown={(event) => {
        if (event.target?.closest?.("[data-video-editor-clip-menu='true']")) {
          return;
        }
        if (event.target?.closest?.("[data-clip-resize]")) {
          return;
        }
        if (event.target?.closest?.("[data-clip-drag]")) {
          return;
        }
        if (event.target?.closest?.("input")) {
          return;
        }
        if (isSubtitle) {
          event.preventDefault();
          event.stopPropagation();
          onSelectClip({ clipId: action?.id, event });
          return;
        }
        onPointerDownClip(event, {
          clipId: action?.id,
          mode: CLIP_INTERACTION_MODE.MOVE,
        });
      }}
      className={[
        "video-editor-clip",
        `video-editor-clip-${type}`,
        isSelected ? "is-selected" : "",
        isDragging ? "is-dragging" : "",
        isCompact ? "is-compact" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      sx={{
        width: "100%",
        height: "100%",
        borderRadius: "8px",
        border: `1.5px solid ${borderColor}`,
        boxShadow,
        overflow: "hidden",
        backgroundColor: "#1f2937",
        boxSizing: "border-box",
        position: "relative",
        cursor: isDragging
          ? "grabbing"
          : isEditingSubtitle
            ? "text"
            : "grab",
        outline: "none",
        opacity: isDragging ? 0 : 1,
        "&:focus-visible": {
          boxShadow: `0 0 0 2px #fff, 0 0 0 4px ${borderColor}`,
        },
      }}
    >
      <button
        type="button"
        data-clip-resize="left"
        aria-label="Trim start"
        className="video-editor-clip-handle video-editor-clip-handle-left"
        onPointerDown={(event) => {
          event.stopPropagation();
          onPointerDownClip(event, {
            clipId: action?.id,
            mode: CLIP_INTERACTION_MODE.RESIZE_LEFT,
          });
        }}
      />
      {isSubtitle ? (
        <button
          type="button"
          data-clip-drag="true"
          aria-label="Move subtitle in time"
          className="video-editor-clip-drag"
          onPointerDown={(event) => {
            event.stopPropagation();
            onPointerDownClip(event, {
              clipId: action?.id,
              mode: CLIP_INTERACTION_MODE.MOVE,
            });
          }}
        />
      ) : null}
      {type === VIDEO_EDITOR_TRACK_TYPE.VIDEO ? (
        <VideoFilmstrip
          sourceUrl={sourceUrl}
          inPoint={inPoint}
          outPoint={outPoint}
          frozen={frozenPreview}
        />
      ) : null}
      {type === VIDEO_EDITOR_TRACK_TYPE.IMAGE ? (
        <ImageClipPreview sourceUrl={sourceUrl} label={label} />
      ) : null}
      {type === VIDEO_EDITOR_TRACK_TYPE.AUDIO ? <AudioWaveformBars /> : null}
      {isSubtitle ? (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            pl: 3.25,
            pr: 1,
            bgcolor: "rgba(14, 165, 233, 0.18)",
          }}
        >
          {isEditingSubtitle ? (
            <input
              ref={inputRef}
              value={draftText}
              onChange={(event) => setDraftText(event.target.value)}
              onBlur={onCommitText}
              onKeyDown={(event) => {
                event.stopPropagation();
                if (event.key === "Enter") {
                  event.preventDefault();
                  event.currentTarget.blur();
                }
                if (event.key === "Escape") {
                  event.preventDefault();
                  ignoreBlurRef.current = true;
                  setDraftText(label);
                  event.currentTarget.blur();
                }
              }}
              onPointerDown={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}
              style={{
                width: "100%",
                border: 0,
                outline: "none",
                background: "transparent",
                color: "#fff",
                fontSize: 12,
                fontWeight: 600,
                minWidth: 0,
              }}
            />
          ) : (
            <Box
              component="strong"
              sx={{
                color: "#fff",
                fontSize: 12,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {label}
            </Box>
          )}
        </Box>
      ) : (
        <Box className="video-editor-clip-meta">
          <strong>{label}</strong>
          <span>{durationLabel}</span>
        </Box>
      )}
      <ClipActionsChip
        isClipHovered={isClipHovered}
        canSplit={canSplit && !isSubtitle}
        isPurple={type === VIDEO_EDITOR_TRACK_TYPE.VIDEO}
        onSplitClip={() => onSplitClip({ clipId: action?.id })}
        onDeleteClip={() => onDeleteClip({ clipId: action?.id })}
      />
      <button
        type="button"
        data-clip-resize="right"
        aria-label="Trim end"
        className="video-editor-clip-handle video-editor-clip-handle-right"
        onPointerDown={(event) => {
          event.stopPropagation();
          onPointerDownClip(event, {
            clipId: action?.id,
            mode: CLIP_INTERACTION_MODE.RESIZE_RIGHT,
          });
        }}
      />
    </Box>
  );
};

const VideoEditorClipActionMemo = memo(VideoEditorClipAction);

export default VideoEditorClipActionMemo;
